<script>
import { logScheduler as columns } from "../../../../tables";

export default {
  name: "h-log-scheduler",
  props: {
    path: {
      type: String,
      default: ""
    }
  },
  computed: {
    uri() {
      return this.path !== ""
        ? this.path
        : this.routeUri("hyperion.lakupandai.log-schedulers.index");
    },
    filteredRanges: {
      get() {
        return this.filters.date.map(o => this.moment(o, "YYYY-MM-DD"));
      },
      set(val) {
        this.filters.date = val.map(moment => moment.format("YYYY-MM-DD"));
      }
    },
    pageSize() {
      return parseInt(this.items.per_page);
    }
  },
  data() {
    return {
      form: this.$form.createForm(this),
      columns,
      items: {},
      loading: false,
      expand: false,
      loadingDownload: false,
      retryingRecords: {},
      modal: {
        visible: false,
        item: {}
      },
      modalResp: {
        visible: false,
        item: {}
      },
      filters: {
        page: 1,
        per_page: 10,
        date: [],
        products: []
      }
    };
  },
  created() {
    this.items = this.getDefaultPagination();
    this.filters = _.merge(this.filters, this.$hyperion.request);
    this.getItems();
  },
  methods: {
    handleAutocomplete(v, iteratee) {
      if (v.length === 0) {
        this.filters[iteratee] = [];
      } else {
        this.filters[iteratee] = _.map(v, o => o.id);
      }
    },
    handleReset(e) {
      e.preventDefault();
      this.filters = {
        page: 1,
        per_page: 10,
        status: "",
        date: []
      };
      this.getItems();
    },
    tableChanged(pagination) {
      this.filters.page = pagination.current;
      this.filters.per_page = pagination.pageSize;
      this.pushState(this.filters);
      this.getItems();
    },
    handleSubmit(e) {
      e.preventDefault();
      this.pushState(this.filters);
      this.getItems();
    },
    getItems() {
      this.loading = true;
      this.$http
        .get(this.routeUri(this.uri, {}, true), {
          params: this.filters
        })
        .then(response => (this.items = response.data))
        .catch(error => this.handleXhrError(error))
        .finally(() => (this.loading = false));
    },
    handleRetry(record) {
      this.$set(this.retryingRecords, record.id, true);
      this.$http
        .post(this.routeUri("hyperion.lakupandai.log-schedulers.retry", { id: record.id }))
        .then(() => {
          this.$message.success("Retry initiated successfully");
          this.getItems();
        })
        .catch(error => this.handleXhrError(error))
        .finally(() => {
          this.$set(this.retryingRecords, record.id, false);
        });
    }
  }
};
</script>

<template>
  <a-card
    size="small"
    type="inner"
    :title="
      items.total > 0
        ? 'Log Scheduler ' + items.total + ' record(s)'
        : 'Log Scheduler'
    "
  >
    <a-form layout="inline" :form="form" @submit="handleSubmit">
      <a-row
        :gutter="24"
        class="form-filter-wrapper"
        :class="expand ? 'expanded' : ''"
      >
        <a-col :span="6">
          <a-range-picker
            :ranges="dateRanges"
            format="YYYY-MM-DD"
            v-model="filteredRanges"
            style="width: 100%"
          />
        </a-col>
        <a-col :span="6">
          <a-select
            v-model="filters.status"
            placeholder="please select your status"
          >
            <a-select-option value="success">Success</a-select-option>
            <a-select-option value="failed">Failed</a-select-option>
          </a-select>
        </a-col>
      </a-row>
      <a-row>
        <a-col :span="24" class="text-right">
          <a-button
            type="primary"
            html-type="submit"
            icon="filter"
            :style="{ display: expand ? 'inline-block' : 'none' }"
            >Search
          </a-button>
          <a-button
            class="ml-2"
            type="dashed"
            html-type="reset"
            icon="reload"
            @click="handleReset"
            :style="{ display: expand ? 'inline-block' : 'none' }"
            >Reset
          </a-button>

          <a class="ml-2" @click="() => (this.expand = !this.expand)">
            Filter
            <a-icon :type="expand ? 'up' : 'down'" />
          </a>
        </a-col>
      </a-row>
    </a-form>

    <a-divider dashed />

    <a-skeleton :loading="loading" active>
      <a-table
        :columns="columns"
        :dataSource="items.data"
        rowKey="id"
        :pagination="{
          pageSize: pageSize,
          current: items.current_page,
          total: items.total,
          showSizeChanger: true
        }"
        @change="tableChanged"
      >
        <h5 slot="last_running_time" slot-scope="record">
          {{
            record.created_at
              ? moment(record.created_at).format("dddd, DD MMMM YYYY HH:mm:ss") + " wib"
              : "-"
          }}
        </h5>

        <h5 slot="retry_count" slot-scope="record">
          {{ record.retry_count || 0 }}
        </h5>

        <h5 slot="file_name" slot-scope="record">
          <a v-if="record.file_path" :href="routeUri('hyperion.lakupandai.log-schedulers.download', { id: record.id })" target="_blank">
            <a-icon type="file-text" /> {{ record.file_name }}
          </a>
          <span v-else>-</span>
        </h5>

        <div slot="status" slot-scope="record">
          <a-tag
            :color="
              record.status === 'success'
                ? 'green'
                : record.status === 'failed'
                ? 'red'
                : 'orange'
            "
          >
            {{ record.status }}
          </a-tag>
        </div>

        <div slot="action" slot-scope="record">
          <a-button
            size="small"
            type="primary"
            @click="() => handleRetry(record)"
            v-show="record.retry_count == 3 && record.status === 'failed'"
            :loading="retryingRecords[record.id]"
            :disabled="retryingRecords[record.id]"
          >
            <a-icon type="reload" v-if="!retryingRecords[record.id]" /> Retry
          </a-button>
        </div>
      </a-table>
    </a-skeleton>

    <a-modal
      title="Raw Response"
      v-model="modalResp.visible"
      @ok="() => (modalResp = { visible: false, item: {} })"
    >
      <div class="code-bg">
        <vue-json-pretty :data="modalResp.item" :deep="1" />
        <div class="extra">
          <a-button
            type="link"
            icon="copy"
            size="small"
            @click="
              copy(
                JSON.stringify(modalResp.item, null, 1),
                'Source has been successfully copied to your clipboard.'
              )
            "
          />
        </div>
      </div>
    </a-modal>
  </a-card>
</template>
